import { Link } from '@tanstack/react-router';
import { ExternalLink, History, Info, Send } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Label } from '~/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover';
import { RadioGroup, RadioGroupItem } from '~/components/ui/radio-group';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '~/components/ui/sheet';
import { Skeleton } from '~/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { useRunTranslations } from '~/features/catalog-operations/hooks/use-translations';
import { ExportDeliverSftpDialog } from '~/features/deliveries/components/export-deliver-sftp-dialog';
import { FileLayoutChoice } from '~/features/transformers/components/file-layout/file-layout-choice';
import { LayoutExportNote } from '~/features/transformers/components/file-layout/layout-export-note';
import { useFileLayout } from '~/features/transformers/hooks/use-file-layout';
import { formatLabel } from '~/features/transformers/utils/transformer-display';
import { useCanTranslateContent } from '~/hooks/useCanTranslateContent';
import { formatLocale, useTranslationLocales } from '~/hooks/useTranslationLocales';
import { cn } from '~/utils/cn';
import { extractApiError } from '~/utils/extract-api-error';

import { useEstimateExport, useTriggerExport } from '../hooks/use-export-mutations';
import { useExportTemplatePreview } from '../hooks/use-export-template-preview';
import { useExportConnectorTemplates } from '../hooks/use-export-templates';
import type {
  ExportConnector,
  ExportFormat,
  ExportTenantContext,
  ExportTriggerRequest,
} from '../types/export.types';
import { supportsTemplateTrigger } from '../types/export.types';
import type { DescriptorChoice } from '../utils/export-column-selection';
import {
  absorbsDescriptors,
  availableColumns,
  descriptorChoice,
  isIdentifierOnlySelection,
  normaliseDescriptorSelection,
  resolveDefaultSelection,
  standardDefaultFields,
  selectionToFields,
  shownColumnKeys,
  visibleColumns,
  withDescriptorChoice,
} from '../utils/export-column-selection';
import { summarizeTemplateDescription } from '../utils/export-template-summary';

import { ExportColumnPicker } from './export-column-picker';
import { ExportPreview } from './export-preview/export-preview';

/**
 * The three branches of the feed rule, in the order they are offered.
 *
 * A module constant rather than inline JSX so the copy keys sit together and
 * the list cannot grow a fourth option in one place and not the other.
 */
/** This drawer turns on no run options. */
const NO_RUN_OPTIONS: ReadonlySet<string> = new Set();

const DESCRIPTOR_CHOICES = [
  {
    value: 'none',
    labelKey: 'estimate.template.descriptors.none',
    hintKey: 'estimate.template.descriptors.noneHint',
  },
  {
    value: 'search_descriptors',
    labelKey: 'estimate.template.descriptors.search',
    hintKey: 'estimate.template.descriptors.searchHint',
  },
  {
    value: 'phrase_descriptors',
    labelKey: 'estimate.template.descriptors.phrase',
    hintKey: 'estimate.template.descriptors.phraseHint',
  },
] as const;

interface ExportSetupSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sourceWorkflowId: string;
  tenantContext: ExportTenantContext;
  /** Human context threaded into the progress toast (experiment / batch name). */
  label: string;
  connector: ExportConnector;
  /** Opens the per-run export history. When provided, a "Recent exports" button
   *  shows in the footer (it closes this drawer first so they aren't stacked). */
  onOpenHistory?: () => void;
  /**
   * The run's enriched attribute keys — `enrichmentConfig.enrichedAttributes`,
   * the same set the delivery picker narrows off. When given, the column picker
   * defaults to it instead of to every connector column, so the file matches
   * what the batch actually enriched.
   *
   * Optional because only the batch-detail page holds the run header; the other
   * entry points pass nothing and keep the all-columns default.
   */
  batchAttributeKeys?: readonly string[];
}

/** Settling time before a column change re-estimates. Long enough that working
 *  through a column list is one request, short enough to feel immediate. */
const ESTIMATE_DEBOUNCE_MS = 400;

/** Format a byte count as a compact human-readable size (KB / MB / GB). */
function formatBytes(bytes: number): string {
  if (bytes <= 0) return '0 KB';
  const units = ['KB', 'MB', 'GB'] as const;
  let value = bytes / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  const amount = value < 10 ? value.toFixed(1) : String(Math.round(value));
  return `${amount} ${units[unitIndex] ?? 'GB'}`;
}

/** Filesystem-safe stem for the downloadable sample, derived from the run label. */
function toFileStem(label: string): string {
  const stem = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return stem || 'export';
}

/**
 * Pre-trigger export drawer, laid out as decide-then-verify rather than as a
 * form with a preview dropped into the middle of it.
 *
 * Three zones, in the order the decision is actually made:
 *
 *   1. **File format** — the file you want to end up with.
 *   2. **Template** — how that file's columns are encoded.
 *   3. **Preview** — the result of 1 + 2, which is the drawer's ONLY scroll
 *      region; then a pinned footer.
 *
 * Format leads because it is the coarser choice and the one the operator
 * arrives already knowing. The two still sit adjacent because they interact:
 * the feed encodings join repeated values with commas, so a CSV delimiter can
 * collide with them — which is what `recommendedForFeedTemplates` is about, and
 * why that hint is rendered against the format options rather than elsewhere.
 *
 * Both are `RadioGroup`s so every option stays visible. A `Select` was tried
 * for each and lost: it hides the alternative behind a click, and the template
 * descriptions are long enough to overflow a trigger.
 *
 * Confirming fires `POST /trigger`; the app-level progress toast then takes
 * over, so the drawer just closes.
 */
export function ExportSetupSheet({
  open,
  onOpenChange,
  sourceWorkflowId,
  tenantContext,
  label,
  connector,
  onOpenHistory,
  batchAttributeKeys,
}: ExportSetupSheetProps) {
  const { t } = useTranslation('exports');
  const { t: tl } = useTranslation('transformers');
  const estimate = useEstimateExport();
  const trigger = useTriggerExport();

  // Both pickers hold `null` for "the user hasn't chosen", and the effective
  // value is derived from the config below rather than copied into state on
  // load — the config arrives after the drawer opens, so syncing it into state
  // would need an effect and could disagree with the payload for one render.
  const [format, setFormat] = useState<ExportFormat | null>(null);
  const [template, setTemplate] = useState<string | null>(null);
  // `null` until the operator touches the picker, exactly like the two above:
  // the default is derived from the config + the batch, both of which arrive
  // after the drawer opens, so storing it would need an effect and could
  // disagree with the request for a render.
  const [columnKeys, setColumnKeys] = useState<string[] | null>(null);
  // `null` = English (en-US), the enriched source — what this drawer always exported.
  const [locale, setLocale] = useState<string | null>(null);
  // A transformer a Lily user picked instead of the assigned layout; null = resolution.
  const [pickedLayout, setPickedLayout] = useState<string | null>(null);

  // File layout (§9): the subsidiary's assigned transformer, if any. When one
  // applies (or a Lily user picks one) it owns format and columns, so the
  // template / column pickers below step aside; on the standard export they
  // work exactly as before.
  const layout = useFileLayout({
    subsidiaryId: tenantContext.subsidiaryId,
    connector,
    pickedId: pickedLayout,
    enabled: open,
  });
  const layoutApplies = layout.applies;

  // Languages this run can be exported in: the ones it was translated into with at least one
  // exportable (PASS/FLAG) product, as on the Exports browse view. GMC only; internal users
  // only. Empty ⇒ no Language choice is shown and the request is byte-identical to before.
  const canTranslate = useCanTranslateContent();
  const runTranslations = useRunTranslations(sourceWorkflowId, {
    enabled: open && canTranslate && connector !== 'META',
  });
  const exportableLocales = useMemo(
    () => [
      ...new Set(
        (runTranslations.data ?? [])
          .filter((r) => r.passCount + r.flagCount > 0)
          .map((r) => r.locale),
      ),
    ],
    [runTranslations.data],
  );
  // "UK English (en-GB)" rather than a bare code (google-agent's locale list).
  const localeNames = useTranslationLocales(exportableLocales.length > 0);
  const localeFilter = locale ? { filters: { locale } } : {};

  // Where the template info popovers are portalled. Inside the Sheet, not the
  // body: a Radix dialog's scroll lock blocks wheel and touch events for
  // anything outside its own subtree, so a body-portalled popover whose text
  // outruns the viewport would refuse to scroll. Held in state via a callback
  // ref because the container is read during render — the same reason the
  // column picker holds its own `portalEl` that way.
  const [templatePortalEl, setTemplatePortalEl] = useState<HTMLFieldSetElement | null>(null);

  // Templates are only fetched while the drawer is open; the config is static
  // and cached for the session, so re-opening costs nothing.
  const {
    templates,
    defaultTemplateCode,
    formats,
    transformationsByCode,
    connector: connectorEntry,
    isPending: templatesPending,
  } = useExportConnectorTemplates(connector, open);

  // Column key → its full config entry. The preview needs more than the label:
  // `kind`, `identifier` and the vendor `spec` limits all come from here, so the
  // whole entry is passed rather than a label map.
  const columnsByKey = useMemo(
    () => Object.fromEntries((connectorEntry?.columns ?? []).map((c) => [c.key, c])),
    [connectorEntry],
  );

  // The batch's enriched attributes when the caller knows them, else every
  // column — see `resolveDefaultSelection`. Derived, so a config that arrives
  // late resolves on the next render rather than through an effect.
  // Memoised, not `?? []` inline: a fresh empty array each render would give
  // every memo below a new dependency identity, so `fields` — and with it the
  // preview's query key — would churn on every paint.
  // Option-gated columns are left out: this drawer sends no `options`, so the
  // server would refuse them ("Unknown META fields").
  const allConnectorColumns = useMemo(
    () => availableColumns(connectorEntry?.columns ?? [], NO_RUN_OPTIONS),
    [connectorEntry],
  );
  // Resolved HERE, above everything that reads the column set, because
  // `allColumns` feeds the count badge, the default selection, `fields` and the
  // identifier-only guard alike — deriving the offered set once is what keeps
  // those four consistent instead of patching each.
  const effectiveTemplate = template ?? defaultTemplateCode;
  const selectedTemplate = templates.find((tpl) => tpl.code === effectiveTemplate);
  // A feed template folds the selected descriptor into `description` and drops
  // the pre-joined columns, and the radio below owns that choice — so the
  // picker must not offer the same value a second way. Raw is untouched.
  const allColumns = useMemo(
    () => visibleColumns(allConnectorColumns, selectedTemplate),
    [allConnectorColumns, selectedTemplate],
  );
  // The run's own attribute selection, echoed by the preview response. Read
  // through the shared hook rather than plumbed up from `ExportPreview`: React
  // Query dedupes on the key, so subscribing here costs no extra request while
  // the preview is showing its default row count, and it avoids a callback that
  // would have to setState from an effect.
  //
  // `batchAttributeKeys` stays as the head start — the batch-detail page knows
  // the set before any preview lands, which stops the picker flashing
  // all-columns on open.
  // Deliberately NOT the preview request: that one carries the column selection,
  // which is derived from `runFields` — keying this on it would be circular, and
  // would refetch on every checkbox. One row, no template, no field subset, so
  // the key is stable for the run and `staleTime: Infinity` makes it a single
  // read per session.
  const runFieldsRequest = useMemo(
    () => ({ sourceWorkflowId, tenantContext, fields: null }),
    [sourceWorkflowId, tenantContext],
  );
  const { data: runFieldsProbe } = useExportTemplatePreview({
    request: runFieldsRequest,
    enabled: open,
    rows: 1,
  });
  // First NON-EMPTY wins, not first non-nullish. `runFields` comes back `[]`
  // for a run that never recorded its attribute selection, and `??` would take
  // that empty array as an answer — discarding the batch header's list, which
  // is the one source that does work on the pages that hold it.
  const probeFields = runFieldsProbe?.runFields;
  const runFields = probeFields && probeFields.length > 0 ? probeFields : batchAttributeKeys;
  // A run that never recorded its attributes starts GMC on the standard enriched set.
  const fallbackFields = standardDefaultFields(connector);
  const defaultSelection = useMemo(
    () =>
      resolveDefaultSelection({
        columns: allColumns,
        batchAttributeKeys: runFields,
        fallbackKeys: fallbackFields,
      }),
    [allColumns, runFields, fallbackFields],
  );
  // Normalised, not stored-normalised: switching template with both descriptors
  // ticked, or carrying a pre-joined column into a template that hides it, both
  // arrive here without any checkbox being touched. Deriving beats an effect
  // that writes back into `columnKeys` — one source of truth, no sync pass.
  const effectiveColumnKeys = useMemo(
    () => normaliseDescriptorSelection(columnKeys ?? defaultSelection.selected, selectedTemplate),
    [columnKeys, defaultSelection.selected, selectedTemplate],
  );
  // Plus the identifiers the template always writes, which the picker shows as
  // always included — see `shownColumnKeys`.
  const visibleKeys = useMemo(
    () => shownColumnKeys(effectiveColumnKeys, selectedTemplate),
    [effectiveColumnKeys, selectedTemplate],
  );

  // `null` for a full selection keeps the request byte-identical to the
  // pre-picker one, which is what `fields: null` meant here before.
  const fieldsToSend = useMemo(
    // Built from the FULL connector list, not the offered one.
    // `selectionToFields` filters the selection THROUGH the list it is given,
    // and on a feed template the offered list has the descriptors removed — so
    // passing it stripped the chosen descriptor out of the request (and, once
    // the selection outgrew that list, sent `fields: null` instead). Hiding a
    // column is a display decision; it must not reach the payload.
    () => selectionToFields(effectiveColumnKeys, allConnectorColumns),
    [effectiveColumnKeys, allConnectorColumns],
  );

  const configDefaultFormat = formats.find((f) => f.default)?.code ?? 'CSV';
  const effectiveFormat = format ?? configDefaultFormat;
  const selectedFormat = formats.find((f) => f.code === effectiveFormat);

  // Full-catalog export of all successful products — no filters in v1. Format is
  // omitted here on purpose: `/estimate` is format-agnostic and runs before the
  // user picks one, so it stays out of the estimate request.
  //
  // `fields` carries the column selection; `null` asks for every column the
  // connector defines. Sent explicitly rather than omitted so the intent is
  // visible on the wire; the server treats null, [] and absent identically.
  const request: ExportTriggerRequest = {
    sourceWorkflowId,
    tenantContext,
    // The layout decides the columns when a transformer applies.
    fields: layoutApplies ? null : fieldsToSend,
    ...localeFilter,
  };

  // The default template IS the raw one, and omitting `template` already means
  // raw — so a non-choice stays off the wire entirely. That keeps this request
  // byte-identical to the pre-template one and avoids the server's
  // template/connector cross-check rejecting a run whose connector our caller
  // mislabelled. An explicit feed choice is sent and validated.
  const templateToSend =
    effectiveTemplate && effectiveTemplate !== defaultTemplateCode ? effectiveTemplate : null;

  const previewRequest = useMemo(
    () => ({
      sourceWorkflowId,
      tenantContext,
      fields: fieldsToSend,
      ...(templateToSend ? { template: templateToSend } : {}),
      ...(locale ? { filters: { locale } } : {}),
    }),
    [sourceWorkflowId, tenantContext, templateToSend, fieldsToSend, locale],
  );

  const { mutate: runEstimate, reset: resetEstimate } = estimate;
  // Fetch the estimate each time the drawer opens; reset its state on close so a
  // re-open doesn't flash the previous run's numbers or keep a stale selection.
  //
  // The selection is handled by the debounced effect below rather than here:
  // `/estimate` now measures the file size by rendering a sample of the
  // requested columns, so the size DOES move with `fields` and a stale number
  // would contradict the picker sitting right above it.
  useEffect(() => {
    if (open) runEstimate(request);
    else {
      resetEstimate();
      setFormat(null);
      setTemplate(null);
      setColumnKeys(null);
      setLocale(null);
      setPickedLayout(null);
    }
    // `request` is derived from the same primitive inputs already in deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sourceWorkflowId, tenantContext, runEstimate, resetEstimate]);

  // Re-estimate when the column selection changes, debounced.
  //
  // Debounced because `/estimate` is a mutation with no response cache, and
  // narrowing a 51-column GMC export means a dozen rapid checkbox clicks — one
  // request each would be a dozen sample renders for a number only the last of
  // them gets to show. The ref suppresses the first pass, whose estimate the
  // effect above has already fired.
  const estimatedFieldsKey = useRef<string | null>(null);
  const fieldsKey = `${layoutApplies || fieldsToSend === null ? '*' : fieldsToSend.join(',')}|${locale ?? ''}`;
  useEffect(() => {
    if (!open) {
      estimatedFieldsKey.current = null;
      return;
    }
    if (estimatedFieldsKey.current === null) {
      estimatedFieldsKey.current = fieldsKey;
      return;
    }
    if (estimatedFieldsKey.current === fieldsKey) return;
    const timer = window.setTimeout(() => {
      estimatedFieldsKey.current = fieldsKey;
      runEstimate(request);
    }, ESTIMATE_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
    };
    // `request` is derived from `fieldsKey` plus the identity deps above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fieldsKey, runEstimate]);

  // A second click in the same tick still sees `trigger.isPending` false (the mutation
  // observer notifies on the next macrotask), so a ref latch is what actually stops a
  // double-click from starting two exports.
  const startingRef = useRef(false);
  const handleConfirm = () => {
    // The button is `aria-disabled`, not `disabled`, so that it can still host
    // the tooltip explaining why — which means the guard has to live here.
    if (confirmDisabled || trigger.isPending || startingRef.current) return;
    startingRef.current = true;
    // Attach the chosen format + template only to the trigger request (not the
    // estimate, whose product count doesn't depend on either).
    trigger.mutate(
      {
        // The standard body always rides along: on its own it is the standard
        // export; with `layout` it is the fallback if integrations answers 422
        // NO_TRANSFORMER. With a layout in force the export starts on
        // integrations (`POST /v1/transformer-exports`), which takes format and
        // columns from the transformer; `transformerId` only on a Lily user's pick.
        request: {
          ...request,
          fields: fieldsToSend,
          format: effectiveFormat,
          ...(templateToSend ? { template: templateToSend } : {}),
        },
        ...(layoutApplies ? { layout: pickedLayout ? { transformerId: pickedLayout } : {} } : {}),
        label,
        connector,
      },
      {
        onSuccess: () => {
          onOpenChange(false);
        },
        onSettled: () => {
          startingRef.current = false;
        },
      },
    );
  };

  const estimateData = estimate.data;
  const isEmpty = estimateData?.expectedProducts === 0;
  const pickersDisabled = estimate.isPending || estimate.isError || isEmpty;

  // A non-default template cannot be honoured while the trigger is served by
  // workflow-webapp — it drops the field and returns a raw file. Blocking the
  // export is the only honest option: a silent raw download from a picker that
  // says "GMC Feed" is worse than not exporting. The preview still works,
  // because that comes from the service that implements the encodings.
  const templateUnsupported =
    !layoutApplies && !supportsTemplateTrigger() && templateToSend != null;

  // `Clear` in the picker leaves only the locked identifiers, and the server
  // reads a fields list naming only identifiers as exactly that — a two-column
  // file of ids. That is a legitimate export to ask for, but it must be asked
  // for deliberately: `Clear` reads as "start over, now let me pick", and
  // nothing else between that click and this button says the file has no
  // content columns.
  const identifierOnly =
    !layoutApplies && isIdentifierOnlySelection(effectiveColumnKeys, allColumns);
  // A failed layout resolve (not a 403) blocks: the subsidiary may have an assigned
  // layout, and silently exporting the standard file would send the wrong file.
  const confirmDisabled =
    pickersDisabled ||
    templateUnsupported ||
    identifierOnly ||
    layout.isLoading ||
    layout.failed ||
    layout.pickPending;
  // Deliver to SFTP reuses every choice above, so it inherits Start export's
  // blockers — plus TXT, which isn't a delivery format (TSV / CSV only).
  const [deliverOpen, setDeliverOpen] = useState(false);
  // With a layout in force the transformer decides the file, so TXT doesn't block.
  const deliverFormat = layoutApplies
    ? 'TSV'
    : effectiveFormat === 'TSV' || effectiveFormat === 'CSV'
      ? effectiveFormat
      : null;
  const deliverDisabled = confirmDisabled || deliverFormat === null;

  // The delimiter recommendation only means something once an encoding is
  // applied — for a raw export the delimiter cannot collide with a separator
  // nothing produced.
  const formatRecommended =
    templateToSend != null && selectedFormat?.recommendedForFeedTemplates === true;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        data-testid="export-setup-sheet"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-[min(1040px,96vw)]"
      >
        <SheetHeader className="shrink-0 space-y-0 border-b px-6 py-4 text-left">
          <SheetTitle className="text-base leading-tight font-semibold">
            {t('estimate.title')}
          </SheetTitle>
          <SheetDescription className="text-xs">
            {t('estimate.description', { name: label })}
          </SheetDescription>
          {/* Sits immediately left of the sheet's own close button, which is
              absolutely positioned at `top-4 right-4` inside `SheetContent` —
              hence the matching offsets rather than a flex row.

              A real link, not a button with a click handler: middle-click,
              cmd-click and "copy link address" all have to work, because the
              whole point is a URL an operator can share. The column selection
              and a non-default template travel with it, so the page opens on
              the columns this drawer is showing — the batch header's attribute
              list only exists on this side, and without it a run that never
              recorded its selection would open on all 51 columns. */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Link
                to="/exports/$sourceWorkflowId"
                params={{ sourceWorkflowId }}
                search={{
                  connector,
                  label,
                  ...(fieldsToSend ? { fields: fieldsToSend.join(',') } : {}),
                  ...(templateToSend ? { template: templateToSend } : {}),
                }}
                target="_blank"
                rel="noreferrer noopener"
                aria-label={t('estimate.openInTab')}
                data-testid="export-setup-open-in-tab-link"
                className="text-muted-foreground hover:text-foreground focus-visible:ring-ring absolute top-4 right-12 cursor-pointer rounded-sm opacity-70 transition-opacity hover:opacity-100 focus-visible:ring-2 focus-visible:outline-none"
              >
                <ExternalLink aria-hidden="true" className="h-4 w-4" />
              </Link>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">{t('estimate.openInTabHint')}</TooltipContent>
          </Tooltip>
        </SheetHeader>

        {/* ── The drawer's one scroll region: setup and preview together. ──
            Zone 1 is as tall as the connector's template list makes it — five
            multi-line descriptions on Meta — so neither zone can be given the
            leftover height on its own without the other being clipped by the
            fixed shell. They scroll as one column between the pinned header
            and footer instead. ── */}
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          {/* ── Zone 1: setup, in the order the decision is actually made ──
            1. the file format you want to download, then
            2. how its columns are encoded,
            and only then the preview of the result. Both are radios: every
            option stays visible, which matters because the two interact — the
            feed encodings join repeated values with commas, so a CSV delimiter
            can collide with them. A `Select` hid one behind a click and
            overflowed its trigger with the long template descriptions. */}
          <div className="bg-muted/30 flex shrink-0 flex-col gap-5 border-b px-6 py-4">
            {/* ── File layout ── the assigned transformer and why, or (Lily users
                only) another one. Leads zone 1 because it decides format and columns. */}
            <fieldset data-testid="export-setup-layout-section">
              <legend className="text-foreground mb-2 text-sm font-medium">
                {tl('layout.title')}
              </legend>
              <FileLayoutChoice
                layout={layout}
                mode="export"
                pickedId={pickedLayout}
                onPick={setPickedLayout}
                idPrefix="export-layout"
              />
            </fieldset>

            {/* ── Step 1 · File format ── */}
            <fieldset data-testid="export-setup-format-section" disabled={pickersDisabled}>
              <div className="mb-2 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                <legend className="text-foreground text-sm font-medium">
                  {t('estimate.format.label')}
                </legend>
                {/* The estimate rides this row rather than sitting in a column of
                  its own — it is a consequence of the whole selection, not of
                  either control, and this keeps it on screen without a void
                  underneath it. */}
                <span>
                  {estimate.isPending && (
                    <Skeleton data-testid="export-setup-estimate-loading" className="h-5 w-44" />
                  )}
                  {estimate.isError && (
                    <span data-testid="export-setup-estimate-error" className="text-error text-sm">
                      {extractApiError(estimate.error, t('estimate.error'))}
                    </span>
                  )}
                  {estimateData && (
                    <span
                      data-testid="export-setup-estimate-summary"
                      className="text-foreground text-sm font-medium"
                    >
                      {isEmpty
                        ? // Guarded on `> 0`, not on presence: null means the job has
                          // no curation run at all (file upload, experiment), and
                          // claiming products were skipped there would be a fiction.
                          (estimateData.alreadyCurated ?? 0) > 0
                          ? t('estimate.emptyAlreadyCurated', {
                              count: estimateData.alreadyCurated ?? 0,
                            })
                          : t('estimate.empty')
                        : layoutApplies
                          ? // The size estimate is the standard file's; a transformer
                            // writes a different file, so only the count is honest.
                            tl('layout.estimate', { count: estimateData.expectedProducts })
                          : t('estimate.summary', {
                              count: estimateData.expectedProducts,
                              size: formatBytes(estimateData.expectedFileSizeBytes),
                            })}
                    </span>
                  )}
                </span>
              </div>

              {layoutApplies ? (
                <p data-testid="export-setup-format-set-by-layout" className="text-sm">
                  <span className="text-foreground font-medium">{formatLabel(layout.format)}</span>{' '}
                  <span className="text-muted-foreground text-xs">{tl('layout.setByLayout')}</span>
                </p>
              ) : (
                <RadioGroup
                  data-testid="export-setup-format-radio-group"
                  value={effectiveFormat}
                  onValueChange={(value) => {
                    setFormat(value as ExportFormat);
                  }}
                  disabled={pickersDisabled}
                  className="flex flex-wrap gap-x-6 gap-y-2"
                >
                  {formats.map((option) => {
                    const id = `export-format-${option.code}`;
                    // The delimiter recommendation only means something once an
                    // encoding is applied — for a raw export the delimiter cannot
                    // collide with a separator nothing produced.
                    const recommended =
                      templateToSend != null && option.recommendedForFeedTemplates;
                    return (
                      <div key={option.code} className="flex items-center gap-2">
                        <RadioGroupItem
                          value={option.code}
                          id={id}
                          data-testid={`export-setup-format-${option.code}-radio`}
                          className={cn(
                            'size-5',
                            pickersDisabled ? 'cursor-not-allowed' : 'cursor-pointer',
                          )}
                        />
                        <Label
                          htmlFor={id}
                          className={cn(
                            'flex flex-wrap items-center gap-1.5 text-sm font-normal',
                            pickersDisabled ? 'cursor-not-allowed' : 'cursor-pointer',
                          )}
                        >
                          <span className="text-foreground font-medium">{option.label}</span>
                          <span className="text-muted-foreground text-xs">
                            {t(`estimate.format.hint.${option.code}`)}
                          </span>
                          {recommended && (
                            <Badge
                              variant="outline"
                              className="pointer-events-none text-[11px] font-normal"
                            >
                              {t('estimate.format.recommended')}
                            </Badge>
                          )}
                        </Label>
                      </div>
                    );
                  })}
                </RadioGroup>
              )}

              {/* Why the recommendation exists, shown only when it applies. */}
              {!layoutApplies && formatRecommended && selectedFormat.note !== '' && (
                <p className="text-muted-foreground mt-1.5 text-xs">{selectedFormat.note}</p>
              )}
            </fieldset>

            {/* ── Step 2 · Template ── */}
            {!layoutApplies && templatesPending && <Skeleton className="h-24 w-full max-w-xl" />}
            {/* Hidden when the connector offers a single template: there is nothing
              to choose, and the raw default is what an omitted template already
              produces. */}
            {/* No `disabled` on the fieldset: that attribute is inherited by
              every control inside it, which would take the info popover
              triggers down with the radios. The radios carry their own
              `disabled` on the `RadioGroup`s below, so the option text can stay
              a single stable line while an estimate is in flight. */}
            {!layoutApplies && templates.length > 1 && (
              <fieldset ref={setTemplatePortalEl} data-testid="export-setup-template-section">
                <legend className="text-foreground mb-2 text-sm font-medium">
                  {t('estimate.template.label')}
                </legend>
                <RadioGroup
                  data-testid="export-setup-template-radio-group"
                  value={effectiveTemplate ?? ''}
                  onValueChange={setTemplate}
                  disabled={pickersDisabled}
                  className="gap-2.5"
                >
                  {templates.map((option) => {
                    const id = `export-template-${option.code}`;
                    const optionEncodedCount = Object.keys(option.transformations).length;
                    // Only the opening sentence is rendered inline; the rest of
                    // the backend paragraph lives in this option's info popover.
                    const { summary, detail } = summarizeTemplateDescription(option.description);
                    // This specific option cannot be exported: only a non-default
                    // (i.e. encoding) template is affected, and only while the
                    // trigger is served by a service that drops the field.
                    const optionBlocked =
                      !supportsTemplateTrigger() && option.code !== defaultTemplateCode;
                    return (
                      <div
                        key={option.code}
                        data-testid={`export-setup-template-option-${option.code}`}
                        className="flex items-start gap-2.5"
                      >
                        <RadioGroupItem
                          value={option.code}
                          id={id}
                          data-testid={`export-setup-template-${option.code}-radio`}
                          className={cn(
                            'mt-0.5 size-5',
                            pickersDisabled ? 'cursor-not-allowed' : 'cursor-pointer',
                          )}
                        />
                        <div className="flex max-w-3xl min-w-0 flex-col gap-0.5">
                          <span className="flex flex-wrap items-center gap-1.5">
                            <Label
                              htmlFor={id}
                              className={cn(
                                'text-foreground text-sm font-medium',
                                pickersDisabled ? 'cursor-not-allowed' : 'cursor-pointer',
                              )}
                            >
                              {option.label}
                              {optionEncodedCount > 0 && (
                                // `secondary` is a crimson token in this theme, and
                                // that is deliberate here: the encoded count is the
                                // one fact that changes what lands in the file.
                                <Badge
                                  variant="secondary"
                                  className="pointer-events-none ml-1.5 text-[11px] font-normal"
                                >
                                  {t('estimate.template.encodedCount', {
                                    count: optionEncodedCount,
                                  })}
                                </Badge>
                              )}
                            </Label>
                            {/* Outside the `Label`, deliberately: a control
                                inside it would select the radio on the way to
                                opening, so reading an option would change the
                                choice. A popover rather than a tooltip because
                                this text is long enough to want to be read at
                                the reader's pace and copied out. */}
                            {detail !== null && (
                              <Popover>
                                <PopoverTrigger asChild>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    data-testid={`export-setup-template-${option.code}-info-button`}
                                    aria-label={t('estimate.template.fullDescription', {
                                      name: option.label,
                                    })}
                                    className="text-muted-foreground hover:text-foreground size-5 cursor-pointer [&_svg]:size-3.5"
                                  >
                                    <Info aria-hidden="true" />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent
                                  container={templatePortalEl}
                                  data-testid={`export-setup-template-${option.code}-info-popover`}
                                  align="start"
                                  collisionPadding={12}
                                  className="max-h-[min(24rem,var(--radix-popover-content-available-height,24rem))] w-[min(24rem,90vw)] space-y-1.5 overflow-y-auto text-xs leading-5"
                                >
                                  <p className="text-foreground font-medium">{option.label}</p>
                                  <p className="text-muted-foreground">{summary}</p>
                                  <p className="text-muted-foreground">{detail}</p>
                                </PopoverContent>
                              </Popover>
                            )}
                          </span>
                          {/* Always the opening sentence, in every state. An
                              estimate is re-run on each template, descriptor
                              and column change, and swapping the full paragraph
                              in for the duration of that request made the
                              option grow by several lines and snap back — a
                              flicker the reader had no way to act on. The rest
                              stays one click away in the popover, which is
                              reachable now that the fieldset no longer disables
                              its trigger. */}
                          <Label
                            htmlFor={id}
                            className={cn(
                              'text-muted-foreground text-xs leading-4 font-normal',
                              pickersDisabled ? 'cursor-not-allowed' : 'cursor-pointer',
                            )}
                          >
                            {summary}
                          </Label>
                          {/* Stated on the option itself, not only in the Start
                            export tooltip: a hover-only explanation for a
                            disabled button is undiscoverable, and this is the
                            one thing that decides whether the run can happen. */}
                          {optionBlocked && (
                            <span
                              data-testid={`export-setup-template-${option.code}-blocked`}
                              className="text-warning text-xs leading-4 font-medium"
                            >
                              {t('estimate.template.notExportable')}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </RadioGroup>
                {/* Rendered AFTER the template group, not inside the selected
                    option: a Radix RadioGroup nested in another hijacks the
                    outer group's roving focus, so arrow keys would stop moving
                    between templates. Indented to read as belonging to the
                    option above it.

                    Shown only for a template that absorbs — on the raw export
                    the descriptors are ordinary columns in the picker, and a
                    "what gets appended to description" control there would
                    describe something that does not happen. */}
                {absorbsDescriptors(selectedTemplate) && (
                  <fieldset
                    data-testid="export-setup-descriptor-section"
                    className="mt-3 ml-[1.875rem]"
                    disabled={pickersDisabled}
                  >
                    <legend className="text-foreground text-sm font-medium">
                      {t('estimate.template.descriptors.legend')}
                    </legend>
                    <p className="text-muted-foreground mt-0.5 mb-2 max-w-xl text-xs leading-4">
                      {t('estimate.template.descriptors.hint')}
                    </p>
                    <RadioGroup
                      data-testid="export-setup-descriptor-radio-group"
                      value={descriptorChoice(effectiveColumnKeys, selectedTemplate)}
                      onValueChange={(next) => {
                        setColumnKeys(
                          withDescriptorChoice({
                            selected: effectiveColumnKeys,
                            choice: next as DescriptorChoice,
                            columns: allConnectorColumns,
                          }),
                        );
                      }}
                      disabled={pickersDisabled}
                      className="gap-2.5"
                    >
                      {DESCRIPTOR_CHOICES.map(({ value, labelKey, hintKey }) => {
                        const id = `export-descriptor-${value}`;
                        return (
                          <div key={value} className="flex items-start gap-2.5">
                            <RadioGroupItem
                              value={value}
                              id={id}
                              data-testid={`export-setup-descriptor-${value}-radio`}
                              className={cn(
                                'mt-0.5 size-5',
                                pickersDisabled ? 'cursor-not-allowed' : 'cursor-pointer',
                              )}
                            />
                            <Label
                              htmlFor={id}
                              className={cn(
                                'flex max-w-3xl flex-col items-start gap-0.5 text-sm font-normal',
                                pickersDisabled ? 'cursor-not-allowed' : 'cursor-pointer',
                              )}
                            >
                              <span className="text-foreground font-medium">{t(labelKey)}</span>
                              <span className="text-muted-foreground text-xs leading-4">
                                {t(hintKey)}
                              </span>
                            </Label>
                          </div>
                        );
                      })}
                    </RadioGroup>
                  </fieldset>
                )}
              </fieldset>
            )}

            {/* ── Language ── only when the run has a translation to export. */}
            {exportableLocales.length > 0 && (
              <fieldset data-testid="export-setup-language-section">
                <legend className="text-foreground mb-1 text-sm font-medium">
                  {t('browse.filters.language')}
                </legend>
                <p className="text-muted-foreground mb-2 text-xs">
                  {t('browse.filters.languageHint')}
                </p>
                <RadioGroup
                  data-testid="export-setup-language-radio-group"
                  value={locale ?? ''}
                  onValueChange={(value) => {
                    setLocale(value || null);
                  }}
                  disabled={pickersDisabled}
                  className="flex flex-wrap gap-x-6 gap-y-2"
                >
                  {[{ value: '', label: t('browse.filters.languageEnglish') }]
                    .concat(
                      exportableLocales.map((code) => ({
                        value: code,
                        label: formatLocale(code, localeNames.data),
                      })),
                    )
                    .map((option) => {
                      const id = `export-locale-${option.value || 'source'}`;
                      return (
                        <div key={id} className="flex items-center gap-2">
                          <RadioGroupItem
                            value={option.value}
                            id={id}
                            data-testid={`export-setup-language-${option.value || 'source'}-radio`}
                            className="size-5"
                          />
                          <Label htmlFor={id} className="text-sm font-normal">
                            {option.label}
                          </Label>
                        </div>
                      );
                    })}
                </RadioGroup>
              </fieldset>
            )}

            {/* ── Step 3 · Columns ──
              Last of the three because it is the only one with a default worth
              trusting: the run's own enriched attributes. Format and template
              change what the file looks like, this changes what is in it. */}
            {layoutApplies && (
              <fieldset data-testid="export-setup-columns-section">
                <legend className="text-foreground mb-1 text-sm font-medium">
                  {tl('layout.columns')}
                </legend>
                <p className="text-muted-foreground text-xs">{tl('layout.columnsSet')}</p>
              </fieldset>
            )}
            {!layoutApplies && !templatesPending && allColumns.length > 0 && (
              <fieldset data-testid="export-setup-columns-section" disabled={pickersDisabled}>
                <ExportColumnPicker
                  testId="export-setup-column-picker"
                  columns={allColumns}
                  selected={effectiveColumnKeys}
                  onChange={setColumnKeys}
                  batchAttributeKeys={batchAttributeKeys}
                  fallbackFields={fallbackFields}
                  unmatched={defaultSelection.unmatched}
                  presets={connectorEntry?.fieldPresets ?? []}
                  transformations={selectedTemplate?.transformations ?? {}}
                  template={selectedTemplate}
                  fileLabel={toFileStem(label)}
                />
              </fieldset>
            )}
          </div>

          {/* ── Zone 2: the preview, at its natural height. Its views carry no
            vertical scroller of their own — this region is the drawer's only
            one — so this just lays them out below the setup zone. ── */}
          <div
            data-testid="export-setup-preview-section"
            className="flex shrink-0 flex-col px-6 py-4"
          >
            {layoutApplies ? (
              // The template preview renders the standard path; with a transformer
              // in force it would show a file this export won't write.
              <LayoutExportNote layout={layout} />
            ) : pickersDisabled ? (
              // Nothing to render a preview from — an estimate that is pending,
              // errored or empty would only fire a request certain to come back
              // with no rows.
              estimate.isPending ? (
                // Roughly the height of a five-row preview, so the drawer does
                // not jump when the real table arrives.
                <Skeleton data-testid="export-setup-preview-loading" className="h-56 w-full" />
              ) : (
                <p
                  data-testid="export-setup-preview-empty"
                  className="text-muted-foreground text-sm"
                >
                  {t('preview.empty')}
                </p>
              )
            ) : (
              <ExportPreview
                request={previewRequest}
                enabled={open}
                template={selectedTemplate}
                columnsByKey={columnsByKey}
                transformationsByCode={transformationsByCode}
                specUrl={connectorEntry?.specUrl ?? null}
                format={selectedFormat}
                fileLabel={toFileStem(label)}
                visibleKeys={visibleKeys}
              />
            )}
          </div>
        </div>

        {/* ── Zone 3: pinned footer. ── */}
        <div
          data-testid="export-setup-footer"
          className="flex shrink-0 items-center justify-between gap-2 border-t px-6 py-4"
        >
          {onOpenHistory ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              data-testid="export-setup-history-button"
              onClick={() => {
                // Close this first so the history sheet isn't stacked over it.
                onOpenChange(false);
                onOpenHistory();
              }}
              className="text-muted-foreground hover:text-foreground cursor-pointer"
            >
              <History aria-hidden="true" className="mr-1.5 h-4 w-4" />
              {t('history.title')}
            </Button>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              data-testid="export-setup-cancel-button"
              onClick={() => {
                onOpenChange(false);
              }}
              className="cursor-pointer"
            >
              {t('estimate.cancel')}
            </Button>
            {/* Same interface for export and delivery: sends this exact
                selection (columns, template, format, language) to SFTP. */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  aria-disabled={deliverDisabled}
                  data-testid="export-setup-deliver-sftp-button"
                  className={cn(
                    deliverDisabled
                      ? 'pointer-events-auto cursor-not-allowed opacity-50'
                      : 'cursor-pointer',
                  )}
                  onClick={() => {
                    if (!deliverDisabled) setDeliverOpen(true);
                  }}
                >
                  <Send aria-hidden="true" className="mr-1.5 h-4 w-4" />
                  {t('estimate.deliverSftp')}
                </Button>
              </TooltipTrigger>
              {deliverDisabled && (
                <TooltipContent className="max-w-xs">
                  {layout.failed
                    ? tl('layout.loadFailed')
                    : layout.pickPending
                      ? tl('layout.pickFirst')
                      : deliverFormat === null
                        ? t('estimate.deliverSftpTxt')
                        : templateUnsupported
                          ? t('estimate.template.triggerUnsupported')
                          : t('estimate.columns.noneSelected')}
                </TooltipContent>
              )}
            </Tooltip>
            {/* The reason a disabled Start export is disabled. `aria-disabled`
                rather than `disabled`, because a natively-disabled button
                receives no pointer events and so can never show its own
                tooltip — the click is blocked in the handler instead. */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  onClick={handleConfirm}
                  isLoading={trigger.isPending}
                  aria-disabled={confirmDisabled}
                  data-testid="export-setup-submit-button"
                  className={cn(
                    confirmDisabled
                      ? 'pointer-events-auto cursor-not-allowed opacity-50'
                      : 'cursor-pointer',
                  )}
                >
                  {t('estimate.confirm')}
                </Button>
              </TooltipTrigger>
              {(layout.failed || layout.pickPending || templateUnsupported || identifierOnly) && (
                <TooltipContent className="max-w-xs">
                  {layout.failed
                    ? tl('layout.loadFailed')
                    : layout.pickPending
                      ? tl('layout.pickFirst')
                      : templateUnsupported
                        ? t('estimate.template.triggerUnsupported')
                        : t('estimate.columns.noneSelected')}
                </TooltipContent>
              )}
            </Tooltip>
          </div>
        </div>
      </SheetContent>
      {deliverOpen && deliverFormat && (
        <ExportDeliverSftpDialog
          open
          onOpenChange={setDeliverOpen}
          label={label}
          selection={{
            sourceWorkflowId,
            fields: fieldsToSend,
            template: effectiveTemplate ?? null,
            format: deliverFormat,
            locale,
            connector,
            pickedTransformerId: pickedLayout,
          }}
          onDelivered={() => {
            onOpenChange(false);
          }}
        />
      )}
    </Sheet>
  );
}
